import React, { useEffect, useState, memo } from "react"
import { isIOSStandalonePWA } from '../../../utils/client-functions';
import { 
    Grid, 
    TextField, 
    TextareaAutosize, 
    OutlinedInput, 
    InputAdornment, 
    IconButton, 
    FormControl, 
    InputLabel, 
    MenuItem, 
    Checkbox, 
    FormControlLabel 
} from "@mui/material"
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Select from '@mui/material/Select';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import allInputCss from "./Allinputs.module.css"
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';

const AuthInput = props => {
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const isDark = Boolean(props.darkMode);
    const fieldBg = isDark ? 'rgba(18, 39, 50, 0.96)' : 'rgba(111, 203, 209, 0.15)';
    const fieldText = isDark ? '#fff' : '#000';
    const fieldPlaceholder = isDark ? 'rgba(255, 255, 255, 0.55)' : '#2E2E2E';

    useEffect(() => {
        // Don't attach programmatic scrollIntoView inside iOS standalone PWAs
        if (isIOSStandalonePWA()) return;

        const handleFocus = (e) => {
            if (window.innerWidth < 768) { // Only for mobile
                setTimeout(() => {
                    const element = e.target;
                    element.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                    });
                }, 300); // Small delay to allow keyboard to appear
            }
        };

        const inputElement = document.getElementById(props.id);
        if (inputElement) {
            inputElement.addEventListener('focus', handleFocus);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('focus', handleFocus);
            }
        };
    }, [props.id]);

    const handleMouseDownPassword = (event) => {
      event.preventDefault();
    };

    const textFieldStyles = {
        width: '100% !important',
        margin: '0 auto',
        fontSize: "16px",
        padding: ".35rem",
        paddingRight: props.buttonInInput ? "2rem" : "auto",
        "& .MuiInputBase-input::placeholder": {
            color: fieldPlaceholder,
            opacity: 1,
        },
        "& .MuiOutlinedInput-input": {
            background: `${fieldBg}!important`,
            borderRadius: "1.25rem",
            padding: '1rem',
            fontSize: "16px",
            color: fieldText,
        },
        "& .MuiInputLabel-root": {
            color: isDark ? 'rgba(255, 255, 255, 0.65)' : "#2E2E2E",
            fontSize: '16px',   
            marginTop: "-.4rem"
        },
        "& .MuiOutlinedInput-root": {
            borderRadius: "50px",
            border: "none",
            outline: "none"
        },
        // Remove any outline/box-shadow from the field container when focused
        "&.MuiFormControl-root:focus-within": {
            outline: 'none',
            boxShadow: 'none'
        },
        "& .MuiOutlinedInput-root.Mui-focused": {
            boxShadow: 'none',
            borderRadius: '50px'
        },
        "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            border: isDark ? ".6px solid rgba(255,255,255,0.18)" : ".6px solid #c3c3c3",
            outline: "none"
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
            border: isDark ? ".6px solid rgba(255,255,255,0.18)" : ".6px solid #c3c3c3",
            color: fieldText,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: isDark ? '#fff' : "#2E2E2E",
            borderRadius: '4px',
            padding: '2px 10px',
            backgroundColor: isDark ? 'rgba(18, 39, 50, 0.96)' : "#6FCBD1",
            fontSize: '16px',
            fontWeight: '600',
        },
        // Keep select shape consistent on focus (remove browser/native focus outline and box-shadow)
        "& .MuiSelect-select:focus": {
            outline: 'none',
            boxShadow: 'none',
            borderRadius: '50px'
        },
        "& select:focus": {
            outline: 'none',
            boxShadow: 'none',
            borderRadius: '50px'
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
            outline: "none",
        },
        // Additional aggressive overrides for browser focus rings inside modal
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiInputBase-root": {
            outline: 'none',
            boxShadow: 'none'
        },
    };

    const textareaStyles = {
        width: '100%',
        margin: '0 auto',
        borderRadius: '20px',
        fontSize: "16px",
        backgroundColor: "#F5F5F5",
        color: "#000",
        padding: ".5rem",
        paddingRight: props.buttonInInput ? "3rem" : ".5rem",
        paddingBottom: props.buttonInInput ? "3rem" : ".5rem",
        border: "none",
        outline: "none",
        "&:hover": {
            border: "none"
        },
        "&:focus": {
            color: "#000",
            border: "none",
            backgroundColor: "white",
            outline: "none",
        }
    };
    
    const checkboxStyles = {
        width: "25px",
        height: "25px",
        '&.MuiCheckbox-colorSecondary.Mui-checked': {
            color: '#56A8AC'
        },
                '& .MuiTypography-root': {
                    fontSize: '.85rem',
                    lineHeight: '.85',
                    color: '#2E2E2E'
                },
    };

    return (
        <Grid item md={props.inputGrid} inputref={props.inputRef} onKeyDown={props.onKeyDown} xs={props.inputGridSm} px={1} mt={props.mt} mb={props.mb} sx={{boxSizing: "border-box"}}>
            {props.multiline ? 
                <TextareaAutosize 
                    id={props.id}
                    label={props.label}
                    placeholder={props.placeholder}
                    className={allInputCss.placeholderDark}
                    minRows={props.rows}
                    maxRows={props.maxRows}
                    style={textareaStyles} 
                    onChange={props.onChange}
                    onPaste={props.onPaste}
                    autoComplete="off"
                    required={props.required}
                    disabled={props.disabled}
                    value={props.value}
                    name={props.name}
                    onFocus={props.onFocus}
                />
            :
            props.inputType === "checkbox" ? 
                <FormControlLabel
                    value="end"
                    sx={checkboxStyles} 
                    control={<Checkbox 
                                name={props.name}
                                checked={props.value}
                                onChange={props.onChange}
                                sx={checkboxStyles} 
                                inputProps={{ 'aria-label': 'controlled' }}
                                color="secondary"
                                disabled={props.disabled}
                            />}
                    label={props.label}
                    labelPlacement="end"
                />
            :
            props.inputType === "password" ?
                <FormControl variant="outlined" sx={textFieldStyles} required={props.required}>
                    <OutlinedInput
                        id={props.id}
                        type={showPassword ? 'text' : 'password'}
                        label={props.label}
                        placeholder={props.label}
                        onChange={props.onChange}
                        value={props.value}
                        onFocus={props.onFocus}
                        sx={{
                            "& .MuiOutlinedInput-input": {
                                color: '#000',
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                                color: '#000',
                            },
                            "& .MuiOutlinedInput-input::placeholder": {
                                color: '#2E2E2E',
                                opacity: 1,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "none" // Added to remove border
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none" // Added to remove border on hover
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "none" // Added to remove border on focus
                            }
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    value={props.value}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                {showPassword ? <VisibilityOff /> : <Visibility sx={{color: "#6FCBD1"}} />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
            :
            props.inputType === "country-select" ?
                <CountryDropdown
                    name={props.name}
                    onChange={props.onChange}
                    className={allInputCss.stateCountryCss}
                    style={{ color: '#000' }}
                    value={props.value}
                    required={props.required}
                />
            :
            props.inputType === "state-select" ?
                <RegionDropdown
                    country={props.country}
                    name={props.name}
                    onChange={props.onChange}
                    className={allInputCss.stateCountryCss}
                    style={{ color: '#000' }}
                    value={props.value}
                    required={props.required}
                />
            :
            props.inputType === "select" ?
                <FormControl sx={textFieldStyles} required={props.required}>
                    <InputLabel id="demo-simple-select-helper-label">{props.label}</InputLabel>
                    <Select
                        id={props.id}
                        onChange={props.onChange}
                        name={props.name}
                        value={props.value}
                        sx={{
                            "& .MuiSelect-select": {
                                color: '#000',
                            },
                            "& .MuiSelect-icon": {
                            right: "18px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            }
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    '& .MuiMenuItem-root': { color: '#000' },
                                }
                            }
                        }}
                    >
                        <MenuItem sx={{ '&&': { color: '#2E2E2E' } }}>
                            <em>{props.label}</em>
                        </MenuItem>
                        {(props.list || []).length !== 0 ? props.list.map((option, index) => {
                            const optionValue = option.value ?? option.mobileCode ?? option.name ?? '';
                            const optionLabel = option.label ?? option.name ?? option.mobileCode ?? optionValue;
                            return <MenuItem sx={{ color: '#000' }} key={index} value={optionValue}>{optionLabel}</MenuItem>
                        }) : [{ value: "", label: props.label }].map((option, index) => {
                            return <MenuItem sx={{ color: '#000' }} key={index} value={option.value}>{option.label}</MenuItem>
                        })}
                    </Select>
                </FormControl>
            :
            props.inputType === "select2" ?
                // Use a native select to avoid Popover/modal stacking issues inside PlainModalOverlay
                <FormControl sx={textFieldStyles} required={props.required}>
                    <InputLabel id={props.id}>{props.label}</InputLabel>
                    <Select
                        native
                        id={props.id}
                        onChange={props.onChange}
                        name={props.name}
                        value={props.value}
                        sx={{
                            "& .MuiSelect-select": {
                                color: fieldText,
                                backgroundColor: fieldBg,
                            },
                            "& .MuiSelect-icon": {
                                right: "18px",
                                color: isDark ? '#fff' : '#000',
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                            }
                        }}
                    >
                        <option value="">{props.label}</option>
                        {(props.list || []).length !== 0 ? props.list.map((option, index) => {
                            const optionValue = option.value ?? option.name ?? option.mobileCode ?? '';
                            const optionLabel = option.label ?? option.name ?? option.mobileCode ?? optionValue;
                            return <option key={index} value={optionValue} style={{ backgroundColor: fieldBg, color: fieldText }}>{optionLabel}</option>
                        }) : [{ value: '', label: props.label }].map((option, index) => {
                            return <option key={index} value={option.value} style={{ backgroundColor: fieldBg, color: fieldText }}>{option.label}</option>
                        })}
                    </Select>
                </FormControl> 
            :
            props.inputType === "mobile" ?
                <PhoneInput 
                    country='ng' 
                    inputClass={allInputCss.Mobile}
                    inputStyle={{ color: '#000' }}
                    onChange={props.onChange}
                    required={props.required}
                    value={props.value}
                    disabled={props.disabled}
                    enableSearch={true}
                />
            :
            props.inputType === "search" ?
                <Paper
                    component="form"
                    sx={{display: props.hidden ? 'none' : 'flex', alignItems: 'center', borderRadius: '50px', padding: '0 10px', border: "none"}} // Added border: "none"
                >
                    <InputBase
                        placeholder={props.placeholder}
                        inputProps={{ 'aria-label': props.placeholder }}
                        sx={{
                            ...textFieldStyles,
                            border: "none", // Added to remove border
                            '& .MuiInputBase-input': {
                                color: '#000',
                            },
                            '& input::placeholder': {
                                color: '#2E2E2E',
                                opacity: 1,
                            },
                            "&:focus": {
                                border: "none",
                                outline: "none"
                            }
                        }}
                        value={props.value}
                        onChange={props.onChange}
                    />
                    <IconButton type="button" aria-label="search">
                        <SearchIcon />
                    </IconButton>
                </Paper>
            :
                <TextField 
                    id={props.id}
                    placeholder={props.placeholder} 
                    label={props.label}
                    background={props.background}
                    sx={textFieldStyles}
                    type={props.inputType}
                    width={props.width}
                    onChange={props.onChange}
                    required={props.required}
                    disabled={props.disabled}
                    value={props.value}
                    name={props.name}
                    onBlur={props.onBlur}
                />                                                          
                
            }
        </Grid>
    )
}

export default memo(AuthInput)